import { DeepLinkPage, Magic as MagicBase } from 'magic-sdk';
import { useLocale } from 'next-intl';
import { useEffect, useState } from 'react';
import { polygonAmoy, polygon } from 'viem/chains';
import { isPendingDirectIdpResolve } from './idp-resolve-storage';
import type { Magic } from './types';
import { getCookie } from '@/utils/cookie';
import { isMagicUserCanceled } from '@/utils/is-magic-user-canceled';

const network = {
  rpcUrl: (process.env.NEXT_PUBLIC_ENVIRONMENT === 'prd' ? polygon : polygonAmoy).rpcUrls.default
    .http[0],
  chainId: (process.env.NEXT_PUBLIC_ENVIRONMENT === 'prd' ? polygon : polygonAmoy).id,
};

export function useMagic({ isRegistered, isBot }: { isRegistered: boolean; isBot: boolean }) {
  const [magic, setMagic] = useState<Magic | null>(null);
  const [magicAuthenticated, setMagicAuthenticated] = useState(false);
  const [token, setToken] = useState<string | undefined>(undefined);
  const [magicAuthReady, setMagicAuthReady] = useState(false);
  const locale = useLocale();

  useEffect(() => {
    (async () => {
      try {
        // IDP / local は専用の _IDP キー、それ以外(dev/stg/prd)は既存キーを使う。
        // 非IDPビルドでは _IDP が空文字になるため || で既存キーへフォールバックする。
        const magic = new MagicBase(
          process.env.NEXT_PUBLIC_MAGIC_API_KEY_IDP || process.env.NEXT_PUBLIC_MAGIC_API_KEY,
          {
            network,
            locale,
          },
        );
        setMagic(magic);
        const hasLoggedIn = getCookie('HAS_LOGGED_IN');
        if (!isRegistered && !isBot && hasLoggedIn === '1' && !isPendingDirectIdpResolve()) {
          const magicLogin = await magic.user.isLoggedIn();
          if (magicLogin) {
            setMagicAuthenticated(true);
            const didToken = await magic.user.getIdToken();
            setToken(didToken);
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        setMagicAuthReady(true);
      }
    })();
  }, [locale]);

  async function magicLogin() {
    try {
      await magic?.wallet.connectWithUI();
      setMagicAuthenticated(true);
      const didToken = await magic?.user.getIdToken();
      setToken(didToken);
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async function magicLogout() {
    try {
      await magic?.user.logout();
      setToken('');
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  // メールアドレス変更
  async function magicUpdateEmail(): Promise<void> {
    try {
      await magic?.user.showSettings({ page: DeepLinkPage.UpdateEmail });
      const freshToken = await magic?.user.getIdToken({ lifespan: 0 });
      setToken(freshToken);
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  // 秘密鍵エクスポート
  async function magicRevealPrivateKey(): Promise<void> {
    try {
      if (!magic?.user.revealEVMPrivateKey) {
        throw new Error('Private key export is not available');
      }
      await magic.user.revealEVMPrivateKey();
    } catch (error) {
      if (!isMagicUserCanceled(error)) console.error(error);
      throw error;
    }
  }

  async function magicExportPrivateKey(): Promise<void> {
    try {
      if (!magic) {
        throw new Error('Magic is not initialized');
      }
      await magic.wallet.connectWithUI();
      let revealError: unknown;
      let hasRevealError = false;
      try {
        await magicRevealPrivateKey();
      } catch (error) {
        hasRevealError = true;
        revealError = error;
      }
      // 成功・キャンセル問わずエクスポート後は必ずログアウトを試みる。
      try {
        await magic.user.logout();
      } catch (logoutError) {
        // 秘密鍵表示が中断/失敗していた場合は元の例外を優先し、
        // ログアウト失敗はそのまま呼び出し元へ返す（成功時にトップへ遷移させない）。
        if (!hasRevealError) throw logoutError;
      }
      if (hasRevealError) throw revealError;
    } catch (error) {
      if (!isMagicUserCanceled(error)) console.error(error);
      throw error;
    }
  }

  return {
    magic,
    magicAuthReady,
    magicAuthenticated,
    magicLogin,
    magicLogout,
    token,
    magicUpdateEmail,
    magicExportPrivateKey,
  };
}
